import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { Polyline } from 'react-leaflet';
import { useMapStore } from '../../store/mapStore';
import { odysseyLocations } from '../../data/locations';
import type { LatLngExpression } from 'leaflet';

const JourneyPlayback = () => {
  const map = useMap();
  const {
    isJourneyPlaying,
    currentJourneyIndex,
    setCurrentJourneyIndex,
    setSelectedLocation,
    selectedLocation,
    pauseJourney
  } = useMapStore();

  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef<number | null>(null);
  const previousModalState = useRef<boolean>(false);

  // Get journey locations sorted by journey day
  const journeyLocations = odysseyLocations
    .filter((loc) => loc.journeyDay > 0)
    .sort((a, b) => a.journeyDay - b.journeyDay);

  // Start journey when play button is pressed
  useEffect(() => {
    if (isJourneyPlaying && !isAnimating && !selectedLocation && currentJourneyIndex === 0) {
      // Journey just started, animate to first location
      setIsAnimating(true);
    }
  }, [isJourneyPlaying, isAnimating, selectedLocation, currentJourneyIndex]);

  // Detect when modal closes and advance to next location
  useEffect(() => {
    const modalIsOpen = selectedLocation !== null;
    const modalJustClosed = previousModalState.current && !modalIsOpen;

    previousModalState.current = modalIsOpen;

    // When modal closes and journey is playing, advance to next location
    if (modalJustClosed && isJourneyPlaying && !isAnimating) {
      const nextIndex = currentJourneyIndex + 1;

      if (nextIndex >= journeyLocations.length) {
        // Journey complete
        pauseJourney();
        setCurrentJourneyIndex(0);
        return;
      }

      // Start animating to next location
      setIsAnimating(true);
      setCurrentJourneyIndex(nextIndex);
    }
  }, [selectedLocation, isJourneyPlaying, currentJourneyIndex, journeyLocations.length, pauseJourney, setCurrentJourneyIndex, isAnimating]);

  // Animate to location when index changes
  useEffect(() => {
    if (!isJourneyPlaying || !isAnimating) return;

    const currentLocation = journeyLocations[currentJourneyIndex];
    if (!currentLocation) {
      setIsAnimating(false);
      return;
    }

    // Animate to current location with slower, more dramatic flight
    const targetZoom = 9;
    map.flyTo(
      [currentLocation.coordinates.lat, currentLocation.coordinates.lng],
      targetZoom,
      {
        duration: 3.5, // 3.5 seconds for slower travel
        easeLinearity: 0.2, // Smoother easing
      }
    );

    // Wait for animation, then open modal
    const timer = setTimeout(() => {
      setSelectedLocation(currentLocation);
      setIsAnimating(false);
    }, 4000); // Wait 4 seconds for map animation

    return () => {
      clearTimeout(timer);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentJourneyIndex, isAnimating, isJourneyPlaying, journeyLocations, map, setSelectedLocation]);

  // Render animated travel line between current and next location
  const renderTravelLine = () => {
    if (!isJourneyPlaying || currentJourneyIndex === 0) return null;

    const currentLocation = journeyLocations[currentJourneyIndex];
    const previousLocation = journeyLocations[currentJourneyIndex - 1];

    if (!currentLocation || !previousLocation) return null;

    const lineCoordinates: LatLngExpression[] = [
      [previousLocation.coordinates.lat, previousLocation.coordinates.lng],
      [currentLocation.coordinates.lat, currentLocation.coordinates.lng],
    ];

    return (
      <Polyline
        positions={lineCoordinates}
        pathOptions={{
          color: '#B87333',
          weight: 3,
          opacity: 0.8,
          dashArray: '10, 10',
          className: 'journey-travel-line',
        }}
      />
    );
  };

  return <>{renderTravelLine()}</>;
};

export default JourneyPlayback;
